import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Copy, Loader2, ScrollText, Stethoscope } from 'lucide-react';

const fmtTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
};

const lineClass = (l) => {
  const s = l.toLowerCase();
  if (s.includes('error') || s.includes('refused') || s.includes('fail') || s.includes('crash') || s.includes(' ! ')) return 'text-red-300';
  if (s.includes(' ok') || s.includes('accepted') || s.includes('done') || s.includes('finished') || s.includes('result:')) return 'text-emerald-300';
  if (s.includes('===') || s.includes('diagnose')) return 'text-amber-200';
  return 'text-foreground/80';
};

export const DeviceAgentLog = ({ device, scriptVersion }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.getPrintDeviceLog(device.id).then((r) => setData(r.data)).catch(() => {}), [device.id]);
  useEffect(() => {
    if (!open) return undefined;
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [open, load]);

  const version = data?.agent_version || device.agent_version || '';
  const outdated = !!version && !!scriptVersion && version !== scriptVersion;
  const lines = data?.log?.lines || [];
  const pending = !!data?.diag_requested_at && !data?.diag_done_at;
  const logAt = data?.log?.at || device.agent_log_at || '';

  const runDiagnose = async () => {
    if (!window.confirm('Verbindungstest starten? Der Agent prüft die iAuto-API Schritt für Schritt und schickt an jede Maschine dieses Rechners einen kurzen Teststrich (verbraucht ggf. je ein Blatt). Das Ergebnis erscheint hier im Log.')) return;
    setBusy(true);
    try {
      await api.requestPrintDeviceDiagnose(device.id);
      toast.success('Verbindungstest angefordert — der Agent meldet sich in wenigen Sekunden hier im Log');
      setOpen(true);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehlgeschlagen'); } finally { setBusy(false); }
  };

  return (
    <div className="border border-border/60 rounded-sm" data-testid={`print-device-log-${device.id}`}>
      <div className="flex items-center gap-2 px-2 py-1.5 flex-wrap">
        <button onClick={() => setOpen((o) => !o)} data-testid={`print-device-log-toggle-${device.id}`}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-mono text-muted-foreground hover:text-foreground">
          {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          <ScrollText className="w-3 h-3" /> Agent-Log
          {logAt && <span className="normal-case tracking-normal text-[10px]">· Stand {fmtTime(logAt)}</span>}
        </button>
        {version && (
          <span data-testid={`print-device-version-${device.id}`}
            className={`text-[9px] font-mono px-1.5 py-0.5 rounded-full border ${outdated
              ? 'text-amber-300 border-amber-500/40 bg-amber-500/10' : 'text-emerald-300 border-emerald-500/30 bg-emerald-500/10'}`}
            title={outdated ? `Die Web-App bietet v${scriptVersion} — Agent neu herunterladen und starten` : 'Agent-Skript ist aktuell'}>
            Agent v{version}{outdated ? ` · veraltet → v${scriptVersion} laden` : ''}
          </span>
        )}
        <span className="ml-auto inline-flex items-center gap-1">
          {open && lines.length > 0 && (
            <button onClick={() => { navigator.clipboard.writeText(lines.join('\n')); toast.success('Log kopiert'); }}
              data-testid={`print-device-log-copy-${device.id}`} title="Log kopieren (z. B. für den Support)"
              className="inline-flex items-center gap-1 px-1.5 h-6 text-[10px] rounded-sm border border-border text-muted-foreground hover:text-foreground">
              <Copy className="w-3 h-3" /> Kopieren
            </button>
          )}
          <button onClick={runDiagnose} disabled={busy || !device.online || pending} data-testid={`print-device-diagnose-${device.id}`}
            title={device.online ? 'Agent prüft die iAuto-API und schreibt einen kurzen Teststrich — Ergebnis im Log' : 'Agent ist offline — zuerst das Agent-Skript starten'}
            className="inline-flex items-center gap-1 px-1.5 h-6 text-[10px] rounded-sm border border-purple-500/40 text-purple-200 hover:bg-purple-500/10 disabled:opacity-40">
            {busy || pending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Stethoscope className="w-3 h-3" />}
            {pending ? 'Test läuft …' : 'Verbindungstest'}
          </button>
        </span>
      </div>
      {open && (
        <pre data-testid={`print-device-log-lines-${device.id}`}
          className="text-[10px] font-mono leading-relaxed bg-black/40 border-t border-border/60 px-2 py-1.5 max-h-56 overflow-auto whitespace-pre-wrap break-words">
          {lines.length === 0
            ? <span className="text-muted-foreground">Noch kein Log — erscheint, sobald der Agent v{scriptVersion || '2.5'} läuft (ältere Skripte melden keine Zeilen).</span>
            : lines.map((l, i) => <div key={i} className={lineClass(l)}>{l}</div>)}
        </pre>
      )}
    </div>
  );
};
